import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { TextField } from "@mui/material";
import { useRef, useEffect } from "react";

import { useTranslation } from "react-i18next";

import {
	UpdateTaskAndSubtaskComment,
	UpdateTaskAndSubtaskCommentSchema,
} from "../schema/tasks-and-subtasks-activity";
import { useToDoSocket } from "../../ToDo/ToDoList/providers/SocketContext";

interface EditCommentInputProps {
	commentId: number;
	initialContent?: string;
	onClose?: () => void;
}

export default function EditCommentInput({
	onClose,
	commentId,
	initialContent,
}: EditCommentInputProps) {
	const { t } = useTranslation();
	const ts = (key: string) => t(`to-do.${key}`);

	const inputRef = useRef<HTMLDivElement | null>(null);

	const defaultValues = { content: initialContent ?? "" };

	const { control, handleSubmit, reset } = useForm<UpdateTaskAndSubtaskComment>({
		defaultValues,
		resolver: zodResolver(UpdateTaskAndSubtaskCommentSchema),
	});

	const { updateComment } = useToDoSocket();

	const onSubmit = async (values: UpdateTaskAndSubtaskComment) => {
		try {
			updateComment(commentId, values.content);
			reset(defaultValues);
		} catch (err) {
			console.error(err);
		} finally {
			onClose?.();
		}
	};

	useEffect(() => {
		const handleClickOutside = (e: MouseEvent) => {
			if (inputRef.current && !inputRef.current.contains(e.target as Node)) {
				onClose?.();
			}
		};

		document.addEventListener("mousedown", handleClickOutside);

		return () => document.removeEventListener("mousedown", handleClickOutside);
	}, [onClose]);

	return (
		<Controller
			control={control}
			name="content"
			render={({ field }) => (
				<TextField
					{...field}
					fullWidth
					multiline
					inputRef={inputRef}
					label={ts("addComment")}
					maxRows={8}
					minRows={2}
					size="small"
					sx={{
						"& .MuiFilledInput-root": {
							backgroundColor: "white",
							"&:hover": { backgroundColor: "white" },
							"&.Mui-focused": { backgroundColor: "white" },
						},
					}}
					onKeyDown={(e) => {
						if (e.key === "Enter" && !e.shiftKey) {
							e.preventDefault();

							handleSubmit(onSubmit)();
						}
					}}
				/>
			)}
		/>
	);
}
